/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { createCheckoutSession } from '@/lib/stripe';
import { GrossCalculationResult } from '@/utils/fees/calculators/grossAmountCalculator';
import { PaymentMethodType } from '@/utils/payment/paymentValidation';
import { calculatePaymentTotals } from '@/utils/payment/paymentCalculations';

interface Product {
  name?: string;
  title?: string;
  amount: number;
  paymentType?: string;
  is_subscription?: boolean;
  connectedAccountId?: string;
  seller?: {
    stripeAccountId?: string;
  };
  stripe_product_id?: string; // Made optional
  stripe_price_id?: string;
  billing_period?: string;
  use_anchor_billing?: boolean;
  anchor_billing_day?: number;
}

interface UsePaymentProps {
  product: Product;
  country: string;
  region: string;
  additionalProducts?: Product[];
  grossCalculation: GrossCalculationResult;
  checkoutStripeAccountId?: string;
  externalUserId?: string;
  externalProductId?: string;
}
const PAYMENT_STATE_KEY = 'keepmore_payment_initialized';
export function usePayment(
  product: Product,
  country: string,
  region: string,
  additionalProducts: Product[] = [],
  grossCalculation: GrossCalculationResult,
  checkoutStripeAccountId?: string,
  externalUserId?: string,
  externalProductId?: string
) {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodType | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentInitialized, setPaymentInitialized] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [connectedAccountId, setConnectedAccountId] = useState<string | null>(null);
  const [hasMixedPaymentTypes, setHasMixedPaymentTypes] = useState(false);

  useEffect(() => {
    const wasInitialized = sessionStorage.getItem(PAYMENT_STATE_KEY);
    //console.log('Checking sessionStorage on mount:', wasInitialized);
    
    if (wasInitialized === 'true') {
      //console.log('Payment was previously initialized, resetting...');
      resetPayment();
      sessionStorage.removeItem(PAYMENT_STATE_KEY);
    }
  }, []);

  useEffect(() => {
    const allProducts = [product, ...additionalProducts];
    const paymentTypes = new Set(allProducts.map(p => p.paymentType));
    setHasMixedPaymentTypes(paymentTypes.size > 1);
  }, [product, additionalProducts]);

  const handlePaymentSelect = (method: PaymentMethodType) => {
    setSelectedPaymentMethod(method);
  };

  const handlePaymentSuccess = () => {
    // Handle successful payment (e.g., update UI, send analytics)
  };
  const resetPayment = () => {
    //console.log('Resetting payment state');
    setSelectedPaymentMethod(null);
    setPaymentInitialized(false);
    setClientSecret(null);
    setPaymentError(null);
    setProcessingPayment(false);
  };

  const initializePayment = async () => {
    if (!selectedPaymentMethod) return false;

    setProcessingPayment(true);
    setPaymentError(null);

    try {
      const allProducts = [product, ...additionalProducts];
      const totalBaseAmount = allProducts.reduce((sum, p) => sum + p.amount, 0);

      const resolvedConnectedAccountId =
        checkoutStripeAccountId ||
        product.connectedAccountId ||
        product.seller?.stripeAccountId ||
        additionalProducts.find(p => p.connectedAccountId)?.connectedAccountId;

      if (!resolvedConnectedAccountId) {
        throw new Error("Missing connected account ID. Payments must route through a connected Stripe account.");
      }
// debugger
      // Always apply fees for card payments
      const applyFees = selectedPaymentMethod === 'card';
      const {processingFee } = calculatePaymentTotals(
        allProducts,
        selectedPaymentMethod
      );
      const platformFee = Math.floor(totalBaseAmount * 0.01 * 100); // 1% fee
      // const processingFee = Math.floor(totalBaseAmount * 0.029 * 100) + 30; // 2.9% + 30¢

      const totalAmountWithFees = applyFees
        ? totalBaseAmount + (platformFee + processingFee) / 100
        : totalBaseAmount;
        


      const payload = {
        products: allProducts.map(p => ({
          title: p.name || p.title,
          amount: p.amount,
          billing_period: p.billing_period,
          is_subscription: Boolean(p.is_subscription),
          connectedAccountId: p.connectedAccountId || resolvedConnectedAccountId,
          stripe_price_id: p.stripe_price_id,
          stripe_product_id: p.stripe_product_id,
          use_anchor_billing: p.use_anchor_billing,
          anchor_billing_day: p.use_anchor_billing ? 1 : p.anchor_billing_day,
        })),
        paymentMethod: selectedPaymentMethod,
        country,
        state: region,
        chargeAchFees: false,
        connectedAccountId: resolvedConnectedAccountId,
        applicationFeeAmount: applyFees ? totalAmountWithFees : 0,
        externalUserId: externalUserId,
        externalProductId: externalProductId,
      };
      // if (selectedPaymentMethod === 'card') {
      //   payload.products.push({
      //     title: 'Processing Fee',
      //     amount: processingFee,
      //     type: 'fee',
      //     is_subscription: Boolean(product.is_subscription),
      //     connectedAccountId: resolvedConnectedAccountId,
      //     applicationFeeAmount: 0,
      //     processingFeeAmount: processingFee,
      //   });
      // }

      const response = await createCheckoutSession(payload);

      if (response && response.checkoutUrl) {
        //console.log('Setting payment initialized flag in sessionStorage');
        sessionStorage.setItem(PAYMENT_STATE_KEY, 'true');

        setClientSecret(response.sessionId);
        setConnectedAccountId(resolvedConnectedAccountId);
        setPaymentInitialized(true);
        window.location.href = response.checkoutUrl;
        return true;
      } else {
        throw new Error("Invalid session response from payment service");
      }
    } catch (error: unknown) {
      console.error('Error initializing payment:', error);
      setPaymentError((error as Error).message || 'An unexpected error occurred.');
      return false;
    } finally {
      setProcessingPayment(false);
    }
  };

  return {
    selectedPaymentMethod,
    processingPayment,
    paymentInitialized,
    paymentError,
    handlePaymentSelect,
    handlePaymentSuccess,
    hasMixedPaymentTypes,
    initializePayment,
    clientSecret,
    connectedAccountId,
    resetPayment
  };
}